import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const FeaturedCard = ({ profile }) => {
  const [busy, setBusy] = useState(false);
  const [autoRenew, setAutoRenew] = useState(!!profile?.featured_auto_renew);
  if (!profile || profile.status !== "approved") return null;

  const active = profile.featured && profile.featured_until && new Date(profile.featured_until) > new Date();
  const legacy = profile.featured && !profile.featured_until;
  const until = active ? new Date(profile.featured_until).toLocaleDateString("en-CA") : null;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/featured/checkout", { origin_url: window.location.origin });
      if (data.resumed) {
        setAutoRenew(true);
        toast.success("Auto-renew resumed — your Featured listing will keep renewing monthly ✦");
        setBusy(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const cancelRenewal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/featured/cancel-renewal");
      setAutoRenew(false);
      toast.success(`Auto-renew cancelled — Featured stays active until ${data.active_until ? new Date(data.active_until).toLocaleDateString("en-CA") : "the end of your paid month"}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="silk-card rounded-lg p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="featured-card">
      <div className="flex items-start gap-3">
        <BadgeCheck size={20} className={active || legacy ? "text-primary mt-0.5" : "text-muted-foreground mt-0.5"} />
        <div>
          <p className="text-foreground text-sm font-medium">Featured & Verified listing</p>
          {active && autoRenew ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="featured-status">
              Renews automatically each month ($25 CAD) — paid through {until}. Verified badge + priority placement.
            </p>
          ) : active ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="featured-status">
              Auto-renew is off — Featured until {until}
            </p>
          ) : legacy ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="featured-status">Active — verified badge + priority placement</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1" data-testid="featured-status">
              Verified badge, priority placement in browse, and top of your category — $25 CAD / month, renews automatically, cancel anytime
            </p>
          )}
        </div>
      </div>
      {active && autoRenew ? (
        <Button size="sm" variant="outline" className="rounded-full px-6"
          data-testid="featured-cancel-renewal-btn" disabled={busy} onClick={cancelRenewal}>
          {busy ? "One moment…" : "Cancel auto-renew"}
        </Button>
      ) : (
        <Button size="sm" variant={active || legacy ? "outline" : "default"} className="rounded-full px-6"
          data-testid="featured-buy-btn" disabled={busy} onClick={subscribe}>
          <Sparkles size={13} className="mr-1.5" />
          {busy ? "One moment…" : active ? "Resume auto-renew — $25/mo" : "Get Featured — $25/mo"}
        </Button>
      )}
    </div>
  );
};
